import { useState, useCallback, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { trans } from '@spotahome/soyuz/client';

import { Mediator } from '@spotahome/soyuz-mediator';

import { useFormDate } from '../../../FormDate/FormDate';

import {
  addDaysToDate,
  formatDate,
  getDateAsUTC
} from '../../../utils/dateUtils';
import { getDaysDiff, dateIsBefore } from '../../../utils/DateRangeHandler';
import { CHANNELS } from '../../../utils/constants';

import baseStyle from './DatePicker.module.scss';

const TODAY = getDateAsUTC(new Date());
const MAX_STAY = 0;
const MIN_STAY = 30;
const MOVE_INTERVAL = 180;

const isMoveOutValid = ({ toDate, fromDate, minStay }) => {
  const diff = getDaysDiff(
    getDateAsUTC(new Date(fromDate)),
    getDateAsUTC(new Date(toDate))
  );

  return diff >= minStay;
};

const defaultFn = () => {};
const defaultFalseFn = () => false;

const DatePicker = ({
  fromDate = '',
  toDate = '',
  showFromDate = false,
  initiallyOpen = false,
  onMoveInOpen = defaultFn,
  onMoveInClose = defaultFn,
  onMoveOutOpen = defaultFn,
  onMoveOutClose = defaultFn,
  autoCompleteDates = false,
  absolutePosition = true,
  disableDayFnMoveIn = defaultFalseFn,
  disableDayFnMoveOut = defaultFalseFn,
  styles = {},
  disabled = false,
  customize = {
    calendarFromFooter: defaultFn,
    calendarToFooter: defaultFn
  },
  onChange,
  settings = {},
  showArrow = true,
  i18n = {},
  showCalendarArrows = true,
  numberOfMonths = 1,
  horizontalCalendar = false,
  calendarBordered = true,
  children = null,
  showRanges = false,
  closeOnSelect = true,
  hideOnBlur = true,
  keepMoveOutCalendarOpen = false
}) => {
  const [fromDateState, setFromDate] = useState(fromDate);
  const [toDateState, setToDate] = useState(toDate);
  const [showFromCalendar, setShowFromCalendar] = useState(initiallyOpen);

  const {
    minDate = TODAY,
    minStay = MIN_STAY,
    maxStay = MAX_STAY,
    moveInterval = MOVE_INTERVAL
  } = settings;

  useEffect(() => setShowFromCalendar(showFromDate), [showFromDate]);
  useEffect(() => setFromDate(fromDate), [fromDate]);
  useEffect(() => setToDate(toDate), [toDate]);

  useEffect(() => {
    if (fromDateState || toDateState) {
      onChange({
        moveIn: formatDate(fromDateState),
        moveOut: toDateState ? formatDate(toDateState) : toDateState
      });
    }
  }, [fromDateState, toDateState]);

  const handleMoveInOpen = useCallback(() => {
    setShowFromCalendar(true);
    onMoveInOpen();
  }, [onMoveInOpen]);

  const handleMoveInClose = useCallback(() => {
    setShowFromCalendar(false);
    onMoveInClose();
  }, [onMoveInClose]);

  const handleMoveOutOpen = useCallback(() => {
    setShowFromCalendar(false);
    onMoveOutOpen();
  }, [onMoveOutOpen]);

  const handleMoveOutClose = useCallback(() => {
    setShowFromCalendar(true);
    onMoveOutClose();
  }, [onMoveOutClose]);

  const handleMoveInChange = useCallback(
    ({ value }) => {
      setFromDate(value);

      const mustUpdateMoveOut =
        autoCompleteDates &&
        (!isMoveOutValid({ minStay, fromDate: value, toDate: toDateState }) ||
          !toDateState);

      if (mustUpdateMoveOut) {
        const calculatedMoveOut = addDaysToDate(value, moveInterval - 1);
        setToDate(calculatedMoveOut);
      }
      if (mustUpdateMoveOut || keepMoveOutCalendarOpen) {
        setShowFromCalendar(false);
      }
    },
    [autoCompleteDates, minStay, moveInterval, toDateState]
  );

  const handleMoveOutChange = useCallback(
    ({ value }) => {
      setToDate(value);
      if (!keepMoveOutCalendarOpen) {
        handleMoveOutClose();
      }
    },
    [keepMoveOutCalendarOpen, handleMoveOutClose]
  );

  const moveInLabel = i18n.dateInLabel || trans('pikaday.label.date_in');
  const moveOutLabel = i18n.dateOutLabel || trans('pikaday.label.date_out');

  const {
    renderInput: renderMoveInInput,
    renderCalendar: renderMoveInCalendar,
    pickerRef: moveInPickerRef
  } = useFormDate({
    id: 'date-input-from',
    name: 'date-input-from',
    dataTest: 'date-input-from',
    dataTestCalendar: 'date-from',
    onChange: handleMoveInChange,
    onCalendarOpen: handleMoveInOpen,
    onCalendarClose: handleMoveInClose,
    absolutePosition,
    isCalendarOpen: showFromCalendar,
    value: fromDateState,
    placeholder: i18n.dateInPlaceholder || 'DD-MM-YYYY',
    title: moveInLabel,
    calendarBordered,
    bold: true,
    minDate,
    disabled,
    disableDayFn: disableDayFnMoveIn,
    renderCalendarContent: customize.calendarFromFooter,
    showCalendarArrows,
    numberOfMonths,
    horizontalCalendar,
    startRange: showRanges && (fromDateState || minDate),
    endRange: showRanges && toDateState,
    hideOnBlur,
    initiallyOpen
  });

  const invalidDateRange = dateIsBefore(toDate, fromDate);
  const minDateMoveOut = minStay
    ? addDaysToDate(fromDateState || minDate || TODAY, minStay - 1)
    : minDate;

  const {
    renderInput: renderMoveOutInput,
    renderCalendar: renderMoveOutCalendar,
    pickerRef: moveOutPickerRef
  } = useFormDate({
    id: 'date-input-to',
    name: 'date-input-to',
    dataTest: 'date-input-to',
    dataTestCalendar: 'date-to',
    onChange: handleMoveOutChange,
    onCalendarOpen: handleMoveOutOpen,
    onCalendarClose: handleMoveOutClose,
    absolutePosition,
    isCalendarOpen: !showFromCalendar,
    value: invalidDateRange ? '' : toDateState,
    placeholder: i18n.dateOutPlaceholder || 'DD-MM-YYYY',
    title: moveOutLabel,
    calendarBordered,
    bold: true,
    disabled: disabled || !fromDateState,
    disableDayFn: disableDayFnMoveOut,
    minDate: minDateMoveOut,
    maxDate: maxStay ? addDaysToDate(fromDateState, maxStay - 1) : undefined,
    renderCalendarContent: customize.calendarToFooter,
    hasError: invalidDateRange,
    showCalendarArrows,
    numberOfMonths,
    horizontalCalendar,
    closeOnSelect,
    startRange: showRanges && fromDateState,
    endRange: showRanges && toDateState,
    hideOnBlur
  });

  useLayoutEffect(() => {
    if (showFromCalendar) {
      moveInPickerRef.current?.show();
      moveOutPickerRef.current?.hide();
    }

    if (!showFromCalendar) {
      moveOutPickerRef.current?.show();
      moveInPickerRef.current?.hide();
    }
  }, [showFromCalendar]);

  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    moveOutPickerRef.current._v = !showFromCalendar;
  }, [showFromCalendar]);

  useEffect(() => {
    const handleResetCalendars = () => {
      if (showFromCalendar) {
        moveInPickerRef.current.calendars = null;
        moveInPickerRef.current.gotoDate(minDate || TODAY);
        moveInPickerRef.current.adjustScrollToToday();
      } else {
        setShowFromCalendar(true);
      }
    };
    Mediator.subscribe(
      CHANNELS.REFRESH_CALENDAR_DATEPICKER,
      handleResetCalendars
    );

    return () => {
      Mediator.unsubscribe(
        CHANNELS.REFRESH_CALENDAR_DATEPICKER,
        handleMoveInChange
      );
    };
  }, [showFromCalendar]);

  if (typeof children === 'function') {
    return children({
      renderMoveInInput,
      renderMoveOutInput,
      renderMoveInCalendar,
      renderMoveOutCalendar
    });
  }
  return (
    <div className={classNames(baseStyle.datepicker, styles.container)}>
      <div
        className={classNames(
          baseStyle.datepicker__wrapper,
          styles.inputWrapper,
          {
            [baseStyle['datepicker__wrapper--no-arrow']]: !showArrow
          }
        )}
      >
        {renderMoveInInput()}
        {showArrow && (
          <span
            className={classNames(baseStyle.datepicker__arrow, styles.arrow)}
          />
        )}
        {renderMoveOutInput()}
      </div>
      {renderMoveInCalendar()}
      {renderMoveOutCalendar()}
    </div>
  );
};

const settingsShape = {
  minDate: PropTypes.instanceOf(Date),
  maxStay: PropTypes.number,
  minStay: PropTypes.number,
  moveInterval: PropTypes.number
};

const i18nShape = {
  dateInLabel: PropTypes.string,
  dateOutLabel: PropTypes.string,
  dateInPlaceholder: PropTypes.string,
  dateOutPlaceholder: PropTypes.string,
  months: PropTypes.arrayOf(PropTypes.string),
  nextMonth: PropTypes.string,
  previousMonth: PropTypes.string,
  weekdays: PropTypes.arrayOf(PropTypes.string),
  weekdaysShort: PropTypes.arrayOf(PropTypes.string)
};

const stylesShape = {
  arrow: PropTypes.string,
  container: PropTypes.string,
  inputWrapper: PropTypes.string
};

const customizeShape = {
  calendarFromFooter: PropTypes.func,
  calendarToFooter: PropTypes.func
};

DatePicker.propTypes = {
  autoCompleteDates: PropTypes.bool,
  showArrow: PropTypes.bool,
  customize: PropTypes.shape(customizeShape),
  disableDayFnMoveIn: PropTypes.func,
  disableDayFnMoveOut: PropTypes.func,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  i18n: PropTypes.shape(i18nShape),
  onChange: PropTypes.func.isRequired,
  onMoveInClose: PropTypes.func,
  onMoveInOpen: PropTypes.func,
  onMoveOutClose: PropTypes.func,
  onMoveOutOpen: PropTypes.func,
  settings: PropTypes.shape(settingsShape),
  showFromDate: PropTypes.bool,
  styles: PropTypes.shape(stylesShape),
  initiallyOpen: PropTypes.bool,
  absolutePosition: PropTypes.bool,
  disabled: PropTypes.bool,
  showCalendarArrows: PropTypes.bool,
  numberOfMonths: PropTypes.number,
  horizontalCalendar: PropTypes.bool,
  calendarBordered: PropTypes.bool,
  children: PropTypes.func,
  showRanges: PropTypes.bool,
  closeOnSelect: PropTypes.bool,
  hideOnBlur: PropTypes.bool,
  keepMoveOutCalendarOpen: PropTypes.bool
};

export default DatePicker;
